import type { AxiosError } from 'axios';
import { useCallback } from 'react';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KEYS, repository } from '@repositories/Repository';
import type IApprovalRepository from '@repositories/approvalRepository/IApprovalRepository';
import type {
  ApprovalDTO,
  ApprovalStepStatusUpdateDTO,
  ApprovalFilterDTO,
} from '@repositories/approvalRepository/approval.types';
import type { Page } from '@type/Page';
import { ApprovalModel } from '@repositories/approvalRepository/models/ApprovalModel';

const approvalRepo = repository.get<IApprovalRepository>(
  KEYS.APPROVAL_REPOSITORY,
);

const approvalKey = {
  all: ['approval'] as const,
  lists: () => [...approvalKey.all, 'lists'] as const,
  list: (filter?: ApprovalFilterDTO) =>
    [...approvalKey.lists(), filter] as const,
  details: () => [...approvalKey.all, 'detail'] as const,
  detail: (id: string) => [...approvalKey.details(), id] as const,
};

export const useGetApprovals = (
  filter?: ApprovalFilterDTO,
  options?: Omit<UseQueryOptions<Page<ApprovalDTO>, AxiosError, Page<ApprovalModel>>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectApprovalList = useCallback(
    (data: Page<ApprovalDTO>) => ({
      ...data,
      content: data.content.map(
        (approval) => new ApprovalModel(approval),
      ),
    }),
    [],
  );

  return useQuery<Page<ApprovalDTO>, AxiosError, Page<ApprovalModel>>({
    queryKey: approvalKey.list(filter),
    queryFn: () => approvalRepo.getApprovals(filter),
    select: selectApprovalList,
    ...options,
  });
};

export const useGetApproval = (
  approvalId: string,
  options?: Omit<UseQueryOptions<ApprovalDTO, AxiosError, ApprovalModel>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectApproval = useCallback(
    (data: ApprovalDTO) => new ApprovalModel(data),
    [],
  );

  return useQuery<ApprovalDTO, AxiosError, ApprovalModel>({
    queryKey: approvalKey.detail(approvalId),
    queryFn: () => approvalRepo.getApproval(approvalId),
    select: selectApproval,
    ...options,
    enabled: !!approvalId && (options?.enabled ?? true),
  });
};

export const useUpdateApprovalStepStatus = () => {
  type UpdateData = {
    approvalId: string;
    approvalStepId: string;
    dto: ApprovalStepStatusUpdateDTO;
  };

  const queryClient = useQueryClient();

  const result = useMutation({
    mutationFn: async (data: UpdateData) => {
      const result = await approvalRepo.updateStepStatus(
        data.approvalId,
        data.approvalStepId,
        data.dto,
      );
      return new ApprovalModel(result);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: approvalKey.all,
      });
    },
  });

  return { ...result };
};
